import React, { useEffect, useRef, useMemo, useState } from 'react';
import {
  defineAdSlot,
  displayAdSlot,
  destroyAdSlot,
  startAdRefresh,
  onSlotRenderEnded,
} from '@/utils/gpt';
import type { GoogleAdsPlacement } from '@/hooks/useTenant';

export interface AdSlotProps {
  /**
   * Unique ID for the ad container div
   */
  divId: string;

  /**
   * Google Ads placement configuration
   */
  adPlacement: GoogleAdsPlacement;

  /**
   * Optional CSS class for the container
   */
  className?: string;

  /**
   * Placement name for logging/debugging
   */
  placement?: string;

  /**
   * Optional: Enable automatic ad refresh
   * DraftKings spec recommends 30-60 second intervals
   */
  enableRefresh?: boolean;

  /**
   * Optional: Refresh interval in seconds (default: 45)
   */
  refreshIntervalSeconds?: number;

  /**
   * Optional: Maximum number of refreshes (0 = unlimited)
   */
  maxRefreshes?: number;

  /**
   * Called once GAM has answered for this slot, with whether it came back empty
   * (no ad sold). Lets a call site stop reserving space around a slot that has
   * collapsed to nothing.
   */
  onRenderEnded?: (isEmpty: boolean) => void;
}

/**
 * How long to wait before concluding that GPT is absent and no answer is ever
 * coming. GPT blocked by an ad blocker, or never initialised, fires no
 * `slotRenderEnded` at all. This is *not* a deadline on GAM: if GPT is live
 * when the timer fires, the slot keeps its reserved space and waits for the
 * real answer however long it takes.
 */
const EMPTY_FALLBACK_TIMEOUT_MS = 5000;

/**
 * AdSlot component for rendering Google Ad Manager ads
 * Based on DraftKings GPT integration requirements
 * Supports responsive size mappings and automatic ad refresh
 */
export const AdSlot: React.FC<AdSlotProps> = ({
  divId,
  adPlacement,
  className = '',
  placement = 'unknown',
  enableRefresh = false,
  refreshIntervalSeconds = 45,
  maxRefreshes = 0,
  onRenderEnded,
}) => {
  // Extract values from adPlacement
  const adUnitPath = useMemo(() => adPlacement.adUnitPath || '', [adPlacement.adUnitPath]);
  const sizes = useMemo(() => adPlacement.sizes || [], [adPlacement.sizes]);
  const sizeMappings = useMemo(() => adPlacement.sizeMappings, [adPlacement.sizeMappings]);
  const targeting = useMemo(() => adPlacement.targeting || undefined, [adPlacement.targeting]);
  const adRef = useRef<HTMLDivElement>(null);
  const isDefinedRef = useRef(false);
  const stopRefreshRef = useRef<(() => void) | null>(null);

  /*
   * 'pending' until GAM answers. An unfilled slot must collapse completely:
   * `collapseEmptyDivs()` only collapses the inner GPT div, so the wrapper's
   * reserved `minHeight` would otherwise hold a blank ~90-106px band open
   * between questions.
   */
  const [fill, setFill] = useState<'pending' | 'filled' | 'empty'>('pending');

  /*
   * Held in a ref so the subscription below is keyed on `divId` alone. Call sites
   * pass an inline arrow, and re-subscribing on every render would risk missing
   * the one `slotRenderEnded` this slot ever fires.
   */
  const onRenderEndedRef = useRef(onRenderEnded);
  onRenderEndedRef.current = onRenderEnded;

  useEffect(() => {
    setFill('pending');

    /*
     * Single resolution path for both outcomes, so the parent callback fires
     * whichever way the slot is settled. The timeout path used to update local
     * state only, which left a call site reserving space around a slot this
     * component had already collapsed.
     */
    const resolve = (isEmpty: boolean) => {
      setFill(isEmpty ? 'empty' : 'filled');
      onRenderEndedRef.current?.(isEmpty);
    };

    /*
     * Never-answers guard, and *only* that: a GPT blocked by an ad blocker, or
     * never present at all, fires no `slotRenderEnded`, so without this the slot
     * would hold its reserved height open forever. The `apiReady` check keeps the
     * timer to exactly that case — a live GPT always answers eventually (
     * `initializeGPT` itself tolerates gpt.js taking up to 10s to load), and
     * collapsing a slot that is merely slow and *will* fill would snap the
     * layout shut and then back open on a served ad: precisely the shift this
     * component exists to avoid. When GPT is present but slow we keep the
     * reserved space and let the listener below — never torn down early —
     * resolve whenever GAM finally answers.
     */
    let answered = false;
    const timeoutId = window.setTimeout(() => {
      if (!answered && !window.googletag?.apiReady) resolve(true);
    }, EMPTY_FALLBACK_TIMEOUT_MS);

    const unsubscribe = onSlotRenderEnded(divId, isEmpty => {
      answered = true;
      window.clearTimeout(timeoutId);
      resolve(isEmpty);
    });

    return () => {
      window.clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [divId]);

  useEffect(() => {
    // Define the ad slot only once
    if (!isDefinedRef.current) {
      defineAdSlot({
        adUnitPath,
        sizes,
        divId,
        targeting,
        sizeMappings, // Pass responsive size mappings
      });

      isDefinedRef.current = true;
    }

    // Display the ad
    displayAdSlot(divId);

    // Start automatic refresh if enabled
    if (enableRefresh) {
      stopRefreshRef.current = startAdRefresh({
        divIds: [divId],
        intervalSeconds: refreshIntervalSeconds,
        maxRefreshes,
      });
    }

    // Cleanup: destroy slot and stop refresh when component unmounts
    return () => {
      // Stop ad refresh if it was started
      if (stopRefreshRef.current) {
        stopRefreshRef.current();
        stopRefreshRef.current = null;
      }

      if (isDefinedRef.current) {
        destroyAdSlot(divId);
        isDefinedRef.current = false;
      }
    };
  }, [
    divId,
    adUnitPath,
    sizes,
    sizeMappings,
    targeting,
    placement,
    enableRefresh,
    refreshIntervalSeconds,
    maxRefreshes,
  ]);

  const isEmpty = fill === 'empty';

  return (
    <div
      data-test-id={`ad-slot-${placement}`}
      className={`ad-slot-container relative w-full flex items-center justify-center ${className}`}
      // Unfilled: no reserved height at all and `display: none`, so the slot takes
      // zero space and its margins collapse with it. Pending/filled keep the floor
      // that stops a served creative shifting the questions below it.
      style={
        isEmpty
          ? { display: 'none' }
          : {
              minHeight: `${Math.max(...sizes.map(s => s[1]))}px`,
            }
      }
    >
      <div
        id={divId}
        ref={adRef}
        className="ad-slot mx-auto"
        style={{
          maxWidth: `${Math.max(...sizes.map(s => s[0]))}px`,
        }}
      />
    </div>
  );
};

export default AdSlot;
